import {InputSystemProvider, toast} from '@wandb/components';
import React, {type PropsWithChildren, useCallback} from 'react';

import {INPUT_ACTION, inputSystem, useRegisterAction} from '../inputSystem';
import {
  keyboardShortcutFailed,
  keyboardShortcutUsed,
} from '../services/analytics';
import {ActivationMethods} from '../services/analytics/types';
import {useColorMode} from '../util/useColorMode';
import {KeyboardShortcutsDrawer} from './KeyboardShortcutsDrawer/KeyboardShortcutsDrawer';
import {useNavContext, useNavUpdaterContext} from './NavContextProvider';
import {useSearchNavAnalytics} from './Search/useSearchNavAnalytics';

export const InputSystem: React.FC<PropsWithChildren> = ({children}) => {
  const onError = useCallback<
    NonNullable<React.ComponentProps<typeof InputSystemProvider>['onError']>
  >(({binding, error}) => {
    keyboardShortcutFailed({
      currentScope: binding.scope,
      shortcutId: binding.id,
      shortcutName: binding.name,
      errorMessage: error instanceof Error ? error.message : String(error),
    });
    toast.error(`${binding.name} failed.`);
  }, []);

  const onAction = useCallback<
    NonNullable<React.ComponentProps<typeof InputSystemProvider>['onAction']>
  >(({binding, currentScope}) => {
    keyboardShortcutUsed({
      currentScope,
      shortcutId: binding.id,
      shortcutName: binding.name,
    });
  }, []);

  return (
    <InputSystemProvider
      system={inputSystem}
      onError={onError}
      onAction={onAction}>
      <InputSystemRegistry>{children}</InputSystemRegistry>
      <KeyboardShortcutsDrawer />
    </InputSystemProvider>
  );
};

const InputSystemRegistry = ({children}: PropsWithChildren) => {
  useInputSystemRegistry();
  return <>{children}</>;
};

// Register global, self-contained actions here.
const useInputSystemRegistry = () => {
  useRegisterDarkMode();
  useRegisterAppSearch();
};

const useRegisterDarkMode = () => {
  const {isDarkMode, updateColorMode} = useColorMode();
  useRegisterAction(INPUT_ACTION.TOGGLE_APP_THEME, () => {
    updateColorMode(isDarkMode ? 'light' : 'dark');
  });
};

const useRegisterAppSearch = () => {
  const {searchActive} = useNavContext();
  const {toggleSearch} = useNavUpdaterContext();
  const {trackSearchNavOnScreen, trackAppLauncherOnScreen} =
    useSearchNavAnalytics();

  useRegisterAction(INPUT_ACTION.TOGGLE_APP_SEARCH, () => {
    if (!searchActive) {
      trackSearchNavOnScreen(ActivationMethods.KeyboardHotkey);
      trackAppLauncherOnScreen(ActivationMethods.KeyboardHotkey);
    }
    toggleSearch();
  });
};
